import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRbacState } from '../../../../context/rbac.context';
import { useStaffService } from '../../../../apis/staff/useStaffService';
import useGuardianLeavesService from '../../../../apis/guardian/sub-apis/useGuardianLeavesService';
import { hasReadPermission } from '../../../../utils/rbacHelpers';
import { IApplyLeavePayload } from '../../../../apis/staff/staffLeaves.interfaces';
import { IGuardianApplyLeavePayload } from '../../../../apis/guardian/guardianLeaves.interfaces';
import { logger } from '../../../../utils/common';
import { localStorageGet } from '../../../../utils/localStorage';

type UserLeavesType = 'staff' | 'guardian';

type TProfileLoggedInUser = {
  id?: string | null;
  instituteId?: string;
};

type TProfileTokenPayload = {
  _id?: string;
  institute_ref?: string;
  instituteId?: string;
};

const decodeProfileTokenPayload = (): TProfileTokenPayload => {
  const token = localStorageGet('token', '') as string;
  const encodedPayload = token.split('.')[1];

  if (!encodedPayload) return {};

  try {
    const payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(
      payload.length + ((4 - (payload.length % 4)) % 4),
      '='
    );
    return JSON.parse(window.atob(paddedPayload)) as TProfileTokenPayload;
  } catch {
    return {};
  }
};

const getProfileUserContext = () => {
  const currentUser = localStorageGet('loggedInUser', undefined) as
    | TProfileLoggedInUser
    | undefined;
  const tokenPayload = decodeProfileTokenPayload();

  return {
    staffId: currentUser?.id ?? tokenPayload._id ?? '',
    instituteId:
      currentUser?.instituteId ??
      tokenPayload.institute_ref ??
      tokenPayload.instituteId ??
      '',
  };
};

export const useProfileLeavesType = (): UserLeavesType => {
  const { rbac } = useRbacState();
  const isStaff = rbac?.staff && hasReadPermission(rbac, 'staff', 'staffList');
  return isStaff ? 'staff' : 'guardian';
};

export const useProfileLeavesStatsHook = () => {
  const leavesType = useProfileLeavesType();
  const staffService = useStaffService();
  const guardianService = useGuardianLeavesService();

  const staffQuery = useQuery({
    queryKey: ['staffLeaveStats'],
    queryFn: () => staffService.getStaffLeaveStats(),
    enabled: leavesType === 'staff',
  });

  const guardianQuery = useQuery({
    queryKey: ['guardianLeaveStats'],
    queryFn: () => guardianService.getLeaveStats(),
    enabled: leavesType === 'guardian',
  });

  if (leavesType === 'staff') return staffQuery;
  return guardianQuery;
};

export const useProfileLeavesListHook = (page = 1, limit = 10) => {
  const leavesType = useProfileLeavesType();
  const staffService = useStaffService();
  const guardianService = useGuardianLeavesService();

  const staffQuery = useQuery({
    queryKey: ['staffLeaves', page, limit],
    queryFn: () => staffService.getStaffLeaves({ page, limit }),
    enabled: leavesType === 'staff',
  });

  const guardianQuery = useQuery({
    queryKey: ['guardianLeaves', page, limit],
    queryFn: () => guardianService.getLeaves({ page, limit }),
    enabled: leavesType === 'guardian',
  });

  if (leavesType === 'staff') return staffQuery;
  return guardianQuery;
};

export const useProfileApplyLeaveHook = () => {
  const queryClient = useQueryClient();
  const leavesType = useProfileLeavesType();
  const staffService = useStaffService();
  const guardianService = useGuardianLeavesService();

  const staffMutation = useMutation({
    mutationFn: (payload: IGuardianApplyLeavePayload) => {
      const { staffId, instituteId } = getProfileUserContext();
      if (!staffId || !instituteId) {
        return Promise.reject(
          new Error('Missing staffId or instituteId for leave application')
        );
      }

      const staffPayload: IApplyLeavePayload = {
        ...payload,
        staffId,
        instituteId,
      };

      return staffService.applyStaffLeave(staffPayload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staffLeaveStats'] });
      queryClient.invalidateQueries({ queryKey: ['staffLeaves'] });
      logger.show_success('Leave applied successfully');
    },
    onError: (err: Error) => logger.error(err.message),
  });

  const guardianMutation = useMutation({
    mutationFn: (payload: IGuardianApplyLeavePayload) => guardianService.applyLeave(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardianLeaveStats'] });
      queryClient.invalidateQueries({ queryKey: ['guardianLeaves'] });
      logger.show_success('Leave applied successfully');
    },
    onError: (err: Error) => logger.error(err.message),
  });

  if (leavesType === 'staff') return staffMutation;
  return guardianMutation;
};
